import { useNavigate } from 'react-router-dom';
import { Row, Col, Table, Tag, Progress, Spin, Empty } from 'antd';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  CartesianGrid, BarChart, Bar,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import {
  Building2, Users, ClipboardList, TrendingUp, AlertTriangle, CheckCircle,
} from 'lucide-react';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { dashboardService, type ExecutiveDashboard } from '@/services/dashboardService';
import { useAuthStore } from '@/store/authStore';
import dayjs from 'dayjs';

/**
 * Dashboard Lãnh đạo (BOD).
 * Format tương đồng với Dashboard Trưởng khoa nhưng đơn vị tổng kết là **khoa/phòng**:
 * - Stats toàn BV
 * - Trend 30 ngày toàn BV
 * - Khối lượng công việc theo khoa (thay vì theo nhân sự ở dashboard trưởng khoa)
 * - Bảng xếp hạng + Bar chart so sánh các khoa
 */

function StatCard({
  label, value, icon, color,
}: { label: string; value: string | number; icon: React.ReactNode; color: string }) {
  return (
    <Card>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 8,
          background: `${color}18`, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, marginTop: 2 }}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

export function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const { hasAnyRole } = useAuthStore();
  const isAllowed = hasAnyRole(['SYS_ADMIN', 'BOD']);

  const { data, isLoading, error } = useQuery<ExecutiveDashboard>({
    queryKey: ['dashboard-executive'],
    queryFn: () => dashboardService.executive(),
    refetchInterval: 5 * 60 * 1000,
    enabled: isAllowed,
    retry: false,
  });

  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h3>Không có quyền truy cập</h3>
        <p style={{ color: '#7d8a98' }}>Dashboard này dành cho Ban Giám đốc.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h3 style={{ color: '#f5222d' }}>Lỗi tải dữ liệu</h3>
        <p style={{ color: '#7d8a98' }}>
          {(error as any)?.response?.data?.message ?? (error as any)?.message ?? 'Unknown error'}
        </p>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
  }

  const trendData = data.trend30Days.map(p => ({
    date: dayjs(p.date).format('DD/MM'),
    created: p.created,
    completed: p.completed,
  }));

  // Sort khoa theo số task quá hạn (cao → thấp) cho top 10 chart
  const sortedDepts = [...data.departmentStats].sort((a, b) => b.overdueTasks - a.overdueTasks);
  const top10ByOverdue = sortedDepts.slice(0, 10).map(d => ({
    name: d.code,
    fullName: d.name,
    'Quá hạn': d.overdueTasks,
    'Đang mở': d.openTasks,
  }));

  return (
    <>
      <PageHeader
        breadcrumb="BAN GIÁM ĐỐC"
        title="Dashboard lãnh đạo"
        subtitle="Tổng quan hoạt động toàn bệnh viện - tóm tắt theo khoa/phòng"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <StatCard label="Task đang mở" value={data.openTasksTotal}
            icon={<ClipboardList size={22} />} color="#6699CC" />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Quá hạn toàn BV" value={data.overdueTasksTotal}
            icon={<AlertTriangle size={22} />} color="#f5222d" />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Hoàn thành tháng này" value={data.completedThisMonth}
            icon={<CheckCircle size={22} />} color="#52c41a" />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Tỷ lệ hoàn thành" value={`${data.overallCompletionRate}%`}
            icon={<TrendingUp size={22} />} color="#722ed1" />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Tổng nhân sự" value={data.totalUsers}
            icon={<Users size={22} />} color="#13c2c2" />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Số khoa/phòng" value={data.totalDepartments}
            icon={<Building2 size={22} />} color="#fa8c16" />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Trend 30 ngày */}
        <Col xs={24}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Xu hướng 30 ngày qua
            </div>
            {trendData.length === 0 ? (
              <Empty description="Chưa có dữ liệu" style={{ padding: 40 }} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="created" stroke="#6699CC" name="Task tạo mới" strokeWidth={2} />
                  <Line type="monotone" dataKey="completed" stroke="#52c41a" name="Task hoàn thành" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        {/* Top 10 khoa quá hạn nhất */}
        <Col xs={24} md={12}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Top khoa có nhiều task quá hạn nhất
            </div>
            {top10ByOverdue.length === 0 ? (
              <Empty description="Chưa có dữ liệu" style={{ padding: 40 }} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={top10ByOverdue} layout="vertical" margin={{ left: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis dataKey="name" type="category" fontSize={11} width={50} />
                  <Tooltip
                    formatter={(value: any, name: any, props: any) => [value, name]}
                    labelFormatter={(label: any, items: any) => items?.[0]?.payload?.fullName ?? label}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Đang mở" fill="#6699CC" />
                  <Bar dataKey="Quá hạn" fill="#f5222d" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        {/* Khối lượng công việc theo khoa - kiểu progress bar */}
        <Col xs={24} md={12}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Khối lượng công việc theo khoa/phòng
            </div>
            {data.departmentStats.length === 0 ? (
              <Empty description="Chưa có dữ liệu" style={{ padding: 40 }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 300, overflowY: 'auto', paddingRight: 6 }}>
                {data.departmentStats.map((d) => {
                  const total = d.openTasks + d.completedTasks + d.overdueTasks || 1;
                  const completedPct = (d.completedTasks / total) * 100;
                  const openPct = (d.openTasks / total) * 100;
                  const overduePct = (d.overdueTasks / total) * 100;
                  return (
                    <div key={d.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{d.name}</span>
                        <span style={{ color: '#7d8a98' }}>
                          {d.staffCount} ns · {d.openTasks + d.completedTasks + d.overdueTasks} task
                          {d.overdueTasks > 0 && (
                            <span style={{ color: '#f5222d', marginLeft: 6 }}>· {d.overdueTasks} trễ</span>
                          )}
                        </span>
                      </div>
                      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eceef1' }}>
                        <div style={{ width: `${completedPct}%`, background: '#52c41a' }} title={`Hoàn thành: ${d.completedTasks}`} />
                        <div style={{ width: `${openPct}%`, background: '#6699CC' }} title={`Đang mở: ${d.openTasks}`} />
                        <div style={{ width: `${overduePct}%`, background: '#f5222d' }} title={`Quá hạn: ${d.overdueTasks}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        {/* Bảng chi tiết khoa */}
        <Col xs={24}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Xếp hạng chi tiết các khoa/phòng
            </div>
            <Table
              size="small"
              dataSource={data.departmentStats}
              rowKey="id"
              pagination={{ pageSize: 15 }}
              locale={{ emptyText: <Empty description="Chưa có dữ liệu" /> }}
              columns={[
                {
                  title: 'Khoa/Phòng', dataIndex: 'name',
                  render: (v: string, r: any) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{v}</div>
                      <div style={{ fontSize: 11, color: '#7d8a98' }}>{r.code}</div>
                    </div>
                  ),
                },
                { title: 'Nhân sự', dataIndex: 'staffCount', width: 90, align: 'center' as const },
                {
                  title: 'Đang mở', dataIndex: 'openTasks', width: 90, align: 'center' as const,
                  render: (v: number) => <Tag color="blue">{v}</Tag>,
                },
                {
                  title: 'Hoàn thành', dataIndex: 'completedTasks', width: 110, align: 'center' as const,
                  render: (v: number) => <Tag color="success">{v}</Tag>,
                },
                {
                  title: 'Quá hạn', dataIndex: 'overdueTasks', width: 90, align: 'center' as const,
                  render: (v: number) => v > 0 ? <Tag color="error">{v}</Tag> : <Tag>0</Tag>,
                },
                {
                  title: 'Tỷ lệ HT', dataIndex: 'completionRate', width: 180,
                  render: (v: number) => (
                    <Progress percent={v} size="small"
                      strokeColor={v >= 80 ? '#52c41a' : v >= 60 ? '#faad14' : '#f5222d'} />
                  ),
                },
                {
                  title: '', width: 100,
                  render: (_: any, r: any) => (
                    <a onClick={() => navigate(`/dashboard/department?id=${r.id}`)}>Chi tiết →</a>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
